import { mastraClient } from '@cobliteam/ai';
import { GeofenceType } from '@cobliteam/common-types/src/geofenceV3';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import useUser from '../useEntity/useUser';
import useRecommendationDecisions from './useRecommendationDecisions';

export type GeofenceRecommendationType = GeofenceType;

const VALID_TYPES = new Set<string>(Object.values(GeofenceType));

// Anything outside GeofenceType (e.g. legacy GAS_STATION) lands on DEFAULT so
// the prefilled form always has a valid selection.
const normalizeType = (raw: string): GeofenceType => (VALID_TYPES.has(raw) ? (raw as GeofenceType) : GeofenceType.DEFAULT);

export interface GeofenceRecommendation {
  id: string;
  fleetId: string;
  name: string;
  reason: string;
  address: string | null;
  color: string;
  type: GeofenceRecommendationType;
  area: number;
  format: string;
  radius: number;
  geometryPoint: {
    type: 'Point';
    coordinates: [number, number];
  };
}

const WORKFLOW_ID = 'geofence-recommendations';
const STALE_TIME_MS = 10 * 60 * 1000;

interface RawRecommendation extends Omit<GeofenceRecommendation, 'type'> {
  type: string;
}

const fetchGeofenceRecommendations = async (): Promise<GeofenceRecommendation[]> => {
  const client = await mastraClient();
  const workflow = client.getWorkflow(WORKFLOW_ID);
  const run = await workflow.createRun();
  const result = await run.startAsync({ inputData: {} });

  if (result.status !== 'success') {
    throw new Error(`geofence-recommendations workflow finished with status "${result.status}"`);
  }

  const raw = (result.result ?? []) as RawRecommendation[];
  return raw.map((rec) => ({ ...rec, type: normalizeType(rec.type) }));
};

const useGeofenceRecommendations = (options?: { enabled?: boolean }) => {
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;
  const decisions = useRecommendationDecisions((s) => (fleetId ? s.byFleet[fleetId] : undefined));

  const enabled = !!fleetId && options?.enabled !== false;

  const query = useQuery({
    queryKey: ['GEOFENCE_RECOMMENDATIONS', fleetId],
    queryFn: fetchGeofenceRecommendations,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data = useMemo(() => {
    if (!query.data) return query.data;
    const rejected = new Set(decisions?.rejected ?? []);
    const accepted = new Set(decisions?.accepted ?? []);
    if (rejected.size === 0 && accepted.size === 0) return query.data;
    return query.data.filter((rec) => !(rejected.has(rec.id) || accepted.has(rec.id)));
  }, [query.data, decisions]);

  return { ...query, data };
};

export default useGeofenceRecommendations;
